"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Bell,
  BriefcaseBusiness,
  CheckCircle2,
  MessageSquare,
  Star,
  Megaphone,
  Check,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNotifications } from "@/hooks/use-notifications";
import type { NotificationType } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const typeConfig: Record<NotificationType, { icon: typeof Bell; color: string }> = {
  match: { icon: BriefcaseBusiness, color: "text-blue-600 bg-blue-100" },
  hire_request: { icon: BriefcaseBusiness, color: "text-orange-600 bg-orange-100" },
  hire_confirmed: { icon: CheckCircle2, color: "text-green-600 bg-green-100" },
  rating: { icon: Star, color: "text-yellow-600 bg-yellow-100" },
  job_posted: { icon: Megaphone, color: "text-purple-600 bg-purple-100" },
  system: { icon: MessageSquare, color: "text-gray-600 bg-gray-100" },
};

function safeTimeAgo(value: string): string {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return "just now";
  }
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading, error } = useNotifications({
    limit: 5, // Show last 5 in dropdown
  });

  // If there's a critical error, render a simplified bell without functionality
  if (error) {
    console.warn("Notifications disabled due to error:", error.message);
    return (
      <Button
        variant="ghost"
        size="icon"
        className="relative h-9 w-9 rounded-full"
        aria-label="Notifications (unavailable)"
        disabled
      >
        <Bell className="h-5 w-5 text-muted-foreground/50" />
      </Button>
    );
  }

  // Get only unread notifications for the dropdown preview
  const recentNotifications = notifications.slice(0, 5);

  function handleNotificationClick(notification: (typeof notifications)[0]) {
    markAsRead(notification.id);
    setOpen(false);

    // Deep link based on notification data
    const data = notification.data as Record<string, string> | null;
    if (data?.conversationId) {
      router.push(`/messages/${data.conversationId}`);
    } else if (notification.type === "hire_confirmed" && (data?.matchId || data?.match_id)) {
      router.push(`/worker/hired/${data.matchId || data.match_id}`);
    } else if (notification.type === "hire_confirmed") {
      // Fallback if no match ID in data — go to worker jobs
      router.push("/worker/jobs");
    } else if (notification.type === "match" && data?.jobId) {
      router.push("/contractor/matches");
    } else if (notification.type === "match") {
      router.push("/dashboard");
    } else if (notification.type === "job_posted") {
      router.push("/worker/jobs");
    } else {
      router.push("/notifications");
    }
  }

  async function handleMarkAllAsRead(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    await markAllAsRead();
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-full"
          aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
        >
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs font-normal"
              onClick={handleMarkAllAsRead}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {loading ? (
          <div className="py-8 text-center">
            <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notifications yet</p>
            <p className="text-xs mt-1">
              You&apos;ll see updates here
            </p>
          </div>
        ) : (
          <>
            {recentNotifications.map((notification) => {
              const config = typeConfig[notification.type];
              const Icon = config.icon;

              return (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    "flex items-start gap-3 p-3 cursor-pointer",
                    !notification.read && "bg-primary/5"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                      config.color
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={cn(
                          "text-sm line-clamp-1",
                          !notification.read && "font-semibold"
                        )}
                      >
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      )}
                    </div>
                    {notification.body && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.body}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {safeTimeAgo(notification.created_at)}
                    </p>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="justify-center text-sm text-primary cursor-pointer">
          <Link href="/notifications" onClick={() => setOpen(false)}>
            View all notifications
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
